<template>
  <v-app-bar
    app
    clipped-left
    :dark="darkTheme"
    short
    dense
    elevation="0"
    flat
    class="app-bar"
    v-bind="$attrs"
  >
    <!-- Desktop layout: logo left, center section, right section -->
    <template v-if="$vuetify.breakpoint.mdAndUp">
      <!-- Keyed rows: crossing the breakpoint builds the other row from scratch
           instead of patching one into the other. -->
      <div class="toolbar-row" key="toolbar-desktop">
        <div class="toolbar-left">
          <a href="/" class="logo-link">
            <v-toolbar-title class="toolbar-logo">
              <img v-if="brandLogo" :src="brandLogo" alt="" class="toolbar-logo-img">
              <v-icon v-else left class="toolbar-logo-icon">{{ toolbarIcon }}</v-icon>
              <span class="toolbar-wordmark">{{ productName }}</span>
              <span v-if="showBetaBadge" class="beta-badge">BETA</span>
            </v-toolbar-title>
          </a>
          <!-- Page navigation (moved out of the drawer, which is now the
               agent workspace). One compact dropdown - the right side is
               already crowded with plan/credits/invite chips + bell + avatar. -->
          <v-menu offset-y min-width="200">
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                text
                small
                class="toolbar-nav-btn"
                v-bind="attrs"
                v-on="on"
              >
                Menu
                <v-icon small right>mdi-chevron-down</v-icon>
              </v-btn>
            </template>
            <v-list dense>
              <v-list-item
                v-for="item in navLinks"
                :key="item.to"
                :to="item.to"
                router
                :disabled="!!item.disabled"
              >
                <v-list-item-icon>
                  <v-icon>{{ item.icon }}</v-icon>
                </v-list-item-icon>
                <v-list-item-title>{{ item.title }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>
        <div class="toolbar-right">
          <div class="plan-credits-container" v-if="!requiresPaidUpgrade || upgradeCta">
            <!-- No plan label until the account data has loaded (no "Loading..." flash). -->
            <span
              v-if="plan"
              class="plan-section"
              style="
                color: var(--text-secondary);
                font-size: var(--font-size-base);
              "
            >
              <strong style="color: var(--text-primary)">Plan:</strong>
              {{ plan }}<template v-if="!upgradeCta && planLink"> (<a
                href="#"
                @click.prevent="goToSubscription"
                class="upgrade-link"
                >{{ planLink }}</a
              >)</template>
            </span>
            <v-btn
              v-if="upgradeCta"
              small
              depressed
              color="primary"
              class="upgrade-cta"
              :to="upgradeCta.to"
              >{{ upgradeCta.label }}</v-btn
            >
            <v-tooltip bottom v-if="usage">
              <template v-slot:activator="{on,attrs}"><span class="credits-section" style="margin-left:24px;color:var(--text-secondary);font-size:var(--font-size-base)" v-bind="attrs" v-on="on"><strong>{{ usage.label }}:</strong> {{ usage.value }}</span></template>
              <span>{{ usage.description }} {{ usage.previewText }} {{ usage.renewalDate ? usage.renewalLabel+' '+usage.renewalDate : '' }} {{ usage.availabilityMessage }}</span>
            </v-tooltip>
            <v-tooltip bottom v-else-if="showCreditInfo">
              <template v-slot:activator="{ on, attrs }">
                <span
                  v-bind="attrs"
                  v-on="on"
                  class="credits-section"
                  style="
                    margin-left: 24px;
                    color: var(--text-secondary);
                    font-size: var(--font-size-base);
                  "
                >
                  <strong style="color: var(--text-primary)">{{ $config.CREDITS_LABEL || 'Credits' }}:</strong>
                  {{ credits }}
                </span>
              </template>
              <!-- Apps with a CREDIT_EQUIVALENT hook lead with the balance
                   in the user's own units ("≈ N conversions"). -->
              <span v-if="creditEquivalentText">{{ creditEquivalentText }}.<br /></span>
              <span v-if="$config && $config.CREDITS_TOOLTIP">{{ $config.CREDITS_TOOLTIP }}</span>
              <span v-else-if="isFreePlanUser">
                Your one-time starter and bonus credits do not refresh daily.
              </span>
              <span v-else>
                {{ creditsPayFor }}. Short jobs cost only a fraction of a
                credit.
              </span>
            </v-tooltip>
            <!-- Free tier: show remaining starter/bonus credits (a paid-only
                 site has none). -->
            <span v-else-if="!forcePaidPlans && creditsEnabled" class="credits-section" style="margin-left: 24px">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <span
                    v-bind="attrs"
                    v-on="on"
                    style="
                      color: var(--text-secondary);
                      font-size: var(--font-size-base);
                      cursor: pointer;
                    "
                    @click="$router.push('/subscribe')"
                  >
                    <strong style="color: var(--text-primary)"
                      >Free credits:</strong
                    >
                    {{ formatFreeCredits(credits) }} remaining
                  </span>
                </template>
                <span>
                  Free accounts receive {{ freeCredits.amount }} starter credits
                  once after email verification. They do not refresh. Subscribe
                  for a monthly allowance.
                </span>
              </v-tooltip>
            </span>
            <!-- Free conversions left (apps that declare config.FREE_ALLOWANCE). -->
            <v-tooltip bottom v-if="freeAllowanceLeft > 0">
              <template v-slot:activator="{ on, attrs }">
                <span
                  v-bind="attrs"
                  v-on="on"
                  class="credits-section free-allowance-count"
                  style="
                    margin-left: 24px;
                    color: var(--text-secondary);
                    font-size: var(--font-size-base);
                  "
                >
                  <strong style="color: var(--text-primary)"
                    >{{ freeAllowanceLabel }}:</strong
                  >
                  {{ freeAllowanceLeft }}
                </span>
              </template>
              <span>{{ freeAllowanceLabel }} don't use your credits.</span>
            </v-tooltip>
            <!-- Hidden where there's no referral programme
                 (config.REFERRALS_ENABLED === false), and for guests, who
                 have no account to credit yet. -->
            <v-tooltip bottom v-if="referralsEnabled && !isAnonymousUser">
              <template v-slot:activator="{ on, attrs }">
                <a
                  href="#"
                  v-bind="attrs"
                  v-on="on"
                  class="invite-friends-chip"
                  @click.prevent="goToInviteFriends"
                >
                  <v-icon small color="orange" class="mr-1"
                    >mdi-gift-outline</v-icon
                  >
                  <strong>+{{ referralCreditsPerSide }} credits</strong>
                  <span class="invite-friends-chip__sub">
                    - invite friends
                  </span>
                </a>
              </template>
              <span>
                Invite a friend to {{ productName }} - you both get
                {{ referralCreditsPerSide }} free credits when they verify
                their email. Up to 5 friends earn you credits.
              </span>
            </v-tooltip>
          </div>
          <!-- Each menu sits in its own element: Vuetify moves a menu's button
               beside the menu outside Vue's bookkeeping, and a bare one could
               land on the wrong side of a sibling rendered later (the bell
               once showed left of the plan). -->
          <div v-if="isAuthenticated" class="toolbar-menu-slot">
            <NotificationBell />
          </div>
          <div class="toolbar-menu-slot">
            <v-menu offset-y left min-width="180">
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  class="ml-2"
                  aria-label="Account menu"
                  v-bind="attrs"
                  v-on="on"
                  style="
                    background: transparent;
                    border-radius: 50%;
                    padding: 0;
                    width: 36px;
                    height: 36px;
                  "
                >
                  <v-avatar
                    size="32"
                    class="user-avatar"
                    :class="{ authenticated: !!currentUser }"
                  >
                    <v-icon v-if="!currentUser" color="primary" size="24"
                      >mdi-account-circle</v-icon
                    >
                    <span v-else class="font-weight-bold user-initials">
                      {{ userInitials }}
                    </span>
                  </v-avatar>
                </v-btn>
              </template>
              <v-list>
                <v-list-item-group>
                  <div v-if="isAuthenticated">
                    <v-list-item>
                      <v-list-item-content>
                        <v-list-item-title
                          class="text-subtitle-1 font-weight-medium"
                        >
                          {{ currentUser ? currentUser.name : "User" }}
                        </v-list-item-title>
                        <v-list-item-subtitle class="text-caption">
                          {{ currentUser ? currentUser.email : "" }}
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                    <v-divider></v-divider>
                    <v-list-item to="/profile" router>
                      <v-list-item-icon>
                        <v-icon>mdi-account-circle</v-icon>
                      </v-list-item-icon>
                      <v-list-item-title>Profile</v-list-item-title>
                    </v-list-item>
                    <v-list-item v-if="apiKeysEnabled" to="/api-keys" router>
                      <v-list-item-icon>
                        <v-icon>mdi-key</v-icon>
                      </v-list-item-icon>
                      <v-list-item-title>API Keys</v-list-item-title>
                    </v-list-item>
                    <v-list-item
                      to="/subscription"
                      router
                      :disabled="requiresPaidUpgrade"
                    >
                      <v-list-item-icon>
                        <v-icon>mdi-card-account-details</v-icon>
                      </v-list-item-icon>
                      <v-list-item-title>My Subscription</v-list-item-title>
                    </v-list-item>
                    <v-list-item
                      to="/billing"
                      router
                      :disabled="requiresPaidUpgrade"
                    >
                      <v-list-item-icon>
                        <v-icon>mdi-credit-card-outline</v-icon>
                      </v-list-item-icon>
                      <v-list-item-title>Billing</v-list-item-title>
                    </v-list-item>
                    <template v-if="isAdmin">
                      <v-divider></v-divider>
                      <v-list-item
                        v-for="item in adminLinks"
                        :key="item.to"
                        :to="item.to"
                        router
                      >
                        <v-list-item-icon>
                          <v-icon>{{ item.icon }}</v-icon>
                        </v-list-item-icon>
                        <v-list-item-title>{{ item.title }}</v-list-item-title>
                      </v-list-item>
                    </template>
                    <v-divider></v-divider>
                    <v-list-item to="/logout" router>
                      <v-list-item-icon>
                        <v-icon>mdi-logout</v-icon>
                      </v-list-item-icon>
                      <v-list-item-title>Logout</v-list-item-title>
                    </v-list-item>
                  </div>
                  <div v-else>
                    <v-list-item to="/login" router>
                      <v-list-item-icon>
                        <v-icon>mdi-login</v-icon>
                      </v-list-item-icon>
                      <v-list-item-title>Login</v-list-item-title>
                    </v-list-item>
                    <v-list-item to="/register" router>
                      <v-list-item-icon>
                        <v-icon>mdi-account-plus</v-icon>
                      </v-list-item-icon>
                      <v-list-item-title>Register</v-list-item-title>
                    </v-list-item>
                  </div>
                </v-list-item-group>
              </v-list>
            </v-menu>
          </div>
        </div>
      </div>
    </template>

    <!-- Mobile layout: hamburger, logo, right section stacked -->
    <template v-else>
      <div class="toolbar-row" key="toolbar-mobile">
        <v-btn
          icon
          class="mr-2 mobile-menu-btn"
          aria-label="Open menu"
          @click="$emit('toggle-drawer')"
        >
          <v-icon>mdi-menu</v-icon>
        </v-btn>

        <a href="/" class="logo-link logo-link--mobile">
          <v-toolbar-title class="toolbar-logo toolbar-logo--mobile">
            <img v-if="brandLogo" :src="brandLogo" alt="" class="toolbar-logo-img toolbar-logo-img--mobile">
            <v-icon v-else left class="mobile-logo-icon">{{ toolbarIcon }}</v-icon>
            <span class="logo-text">{{ productName }}</span>
            <span v-if="showBetaBadge" class="beta-badge beta-badge--mobile">BETA</span>
          </v-toolbar-title>
        </a>

        <!-- Compact balance: desktop's plan/credits row has no room here,
             but the numbers users act on (free conversions left + credits)
             must stay visible. Tapping goes where they can get more. -->
        <div
          v-if="currentUser && !requiresPaidUpgrade && (creditsEnabled || freeAllowanceLeft > 0)"
          class="mobile-balance"
          role="button"
          @click="$router.push(isAnonymousUser ? '/subscribe' : '/billing')"
        >
          <v-tooltip bottom v-if="freeAllowanceLeft > 0">
            <template v-slot:activator="{ on, attrs }">
              <span class="mobile-balance-chip" v-bind="attrs" v-on="on">
                Free: {{ freeAllowanceLeft }}
              </span>
            </template>
            <span>{{ freeAllowanceLabel }} left</span>
          </v-tooltip>
          <v-tooltip v-if="creditsEnabled" bottom>
            <template v-slot:activator="{ on, attrs }">
              <span class="mobile-balance-chip" v-bind="attrs" v-on="on">
                {{ $config.CREDITS_LABEL || "Credits" }}: {{ credits }}
              </span>
            </template>
            <span>
              {{ $config.CREDITS_LABEL || "Credits" }}: {{ credits
              }}<template v-if="creditEquivalentText">
                ({{ creditEquivalentText }})</template
              >
            </span>
          </v-tooltip>
        </div>

        <div class="toolbar-right">
          <div v-if="isAuthenticated" class="toolbar-menu-slot">
            <NotificationBell />
          </div>
          <div class="toolbar-menu-slot">
            <v-menu offset-y left min-width="180">
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  class="ml-1 mr-1 mobile-profile-btn"
                  aria-label="Account menu"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-avatar
                    class="user-avatar mobile-user-avatar"
                    :class="{ authenticated: !!currentUser }"
                  >
                    <v-icon
                      v-if="!currentUser"
                      color="primary"
                      class="mobile-profile-icon"
                      >mdi-account-circle</v-icon
                    >
                    <span v-else class="font-weight-bold user-initials">
                      {{ userInitials }}
                    </span>
                  </v-avatar>
                </v-btn>
              </template>
              <v-list>
                <v-list-item to="/profile" router>
                  <v-list-item-icon>
                    <v-icon>mdi-account-circle</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>Profile</v-list-item-title>
                </v-list-item>
                <v-list-item
                  to="/subscription"
                  router
                  :disabled="requiresPaidUpgrade"
                >
                  <v-list-item-icon>
                    <v-icon>mdi-card-account-details</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>My Subscription</v-list-item-title>
                </v-list-item>
                <v-list-item to="/billing" router :disabled="requiresPaidUpgrade">
                  <v-list-item-icon>
                    <v-icon>mdi-credit-card-outline</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>Billing</v-list-item-title>
                </v-list-item>
                <v-list-item to="/pricing" router>
                  <v-list-item-icon>
                    <v-icon>mdi-currency-usd</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>Pricing</v-list-item-title>
                </v-list-item>
                <v-list-item
                  v-if="referralsEnabled && !requiresPaidUpgrade"
                  to="/invite-friends"
                  router
                >
                  <v-list-item-icon>
                    <v-icon>mdi-gift-outline</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>Invite Friends</v-list-item-title>
                </v-list-item>
                <v-list-item :to="($config && $config.SUPPORT_PATH) || '/faq'" router>
                  <v-list-item-icon>
                    <v-icon>mdi-help-circle-outline</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>FAQ</v-list-item-title>
                </v-list-item>
                <template v-if="isAdmin">
                  <v-divider></v-divider>
                  <v-list-item
                    v-for="item in adminLinks"
                    :key="item.to"
                    :to="item.to"
                    router
                  >
                    <v-list-item-icon>
                      <v-icon>{{ item.icon }}</v-icon>
                    </v-list-item-icon>
                    <v-list-item-title>{{ item.title }}</v-list-item-title>
                  </v-list-item>
                </template>
                <v-divider></v-divider>
                <v-list-item to="/logout" router>
                  <v-list-item-icon>
                    <v-icon>mdi-logout</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>Logout</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </div>
        </div>
      </div>
    </template>
  </v-app-bar>
</template>

<script>
import { mapGetters } from "vuex";
import NotificationBell from "@shared/components/notifications/NotificationBell.vue";
import {accountUsage} from "@shared/utils/accountUsage";
import { safeInternalPath } from "@shared/utils/safePath";
import { creditsPayFor, creditsEnabled } from "@shared/utils/productWording";
// Seam: config.js is per-app - see the note in services/api.js.
import { PRODUCT_NAME } from "@/config";

export default {
  name: "Toolbar",
  components: { NotificationBell },
  inheritAttrs: false,
  props: {
    plan: {
      type: String,
      default: "Basic"
    },
    credits: {
      type: [Number, String],
      default: 0
    }
  },
  data() {
    return {};
  },
  computed: {
    usage(){return accountUsage(this.$store,this.$config);},
    creditsPayFor() {
      return creditsPayFor();
    },
    creditsEnabled() {
      return creditsEnabled();
    },
    // Per-app seam: config.CREDIT_EQUIVALENT turns a credit amount into the
    // user's own units ("≈ N conversions"); apps without it see no change.
    creditEquivalentText() {
      const fn = this.$config && this.$config.CREDIT_EQUIVALENT;
      if (typeof fn !== "function") return null;
      return fn(Number(this.credits) || 0);
    },
    freeAllowanceLabel() {
      const setup = this.$config && this.$config.FREE_ALLOWANCE;
      return (setup && setup.label) || "Free conversions";
    },
    freeAllowanceLeft() {
      const value = this.$store.getters.freeAllowance;
      return value ? Number(value.remaining) || 0 : 0;
    },
    ...mapGetters([
      "isAuthenticated",
      "currentUser",
      "darkTheme",
      "requiresPaidUpgrade",
      "forcePaidPlans",
      "isFreePlanUser",
      "showCreditInfo",
      "freeCredits",
      "isAdmin"
    ]),
    isAnonymousUser() {
      return !!(this.currentUser && this.currentUser.is_anonymous);
    },
    // Optional app button ($config.UPGRADE_CTA = {label, to}) for free-plan
    // members, returning them here after checkout. Without the config the
    // original "(Upgrade)" link is shown for every plan, as before.
    upgradeCta() {
      const cta = this.$config && this.$config.UPGRADE_CTA;
      if (!cta || !cta.label || !cta.to) return null;
      // Also shown to members a paid-only site gates, on the pages it lets them see.
      if (!this.$store.getters.accountDataLoaded || !this.isFreePlanUser ||
          this.isAnonymousUser) return null;
      if (["/subscribe", "/subscription", "/pricing"].indexOf(this.$route.path) >= 0) return null;
      const next = safeInternalPath(this.$route.fullPath);
      return { label: cta.label, to: next ? { path: cta.to, query: { next } } : cta.to };
    },
    // Optional app logo icon ($config.TOOLBAR_ICON); other apps keep the rocket.
    toolbarIcon() {
      return (this.$config && this.$config.TOOLBAR_ICON) || "mdi-rocket-launch";
    },
    // Optional app logo image ($config.BRAND_LOGO) in place of the icon; none by default.
    brandLogo() {
      return (this.$config && this.$config.BRAND_LOGO) || "";
    },
    // Absent config => shown; an app opts out with BETA_BADGE: false.
    showBetaBadge() {
      return !(this.$config && this.$config.BETA_BADGE === false);
    },
    // Absent config => enabled, so apps that never opt out are unaffected.
    referralsEnabled() {
      return !(this.$config && this.$config.REFERRALS_ENABLED === false);
    },
    // An app without API keys hides the menu entry (API_KEYS_ENABLED: false).
    apiKeysEnabled() {
      return !(this.$config && this.$config.API_KEYS_ENABLED === false);
    },
    // The link after the plan name: "Upgrade", or "Manage" on the dearest plan,
    // where there is nothing to upgrade to. A paid plan shows no link until the
    // plan list has loaded, rather than flashing "Upgrade".
    planLink() {
      if (this.isFreePlanUser) return "Upgrade";
      if (!this.$store.getters.plansLoaded) return "";
      return this.$store.getters.onTopPlan ? "Manage" : "Upgrade";
    },
    needsPlanList() {
      return !!(this.isAuthenticated && this.$store.getters.accountDataLoaded &&
        !this.isFreePlanUser && !this.$store.getters.plansLoaded);
    },
    // Per-app payout ($config.REFERRAL_CREDITS_PER_SIDE) - must mirror the
    // backend's SITE_<SLUG>_REFERRAL_CREDITS_PER_SIDE; absent = shared default.
    referralCreditsPerSide() {
      return (this.$config && this.$config.REFERRAL_CREDITS_PER_SIDE) || 100;
    },
    productName() {
      return PRODUCT_NAME;
    },
    // Page navigation moved here from the drawer (which is now the agent
    // workspace). Same entries/gating the old drawer navItems had.
    navLinks() {
      const requiresPaidUpgrade = this.requiresPaidUpgrade;
      const items = [
        {
          icon: "mdi-card-account-details",
          title: "Subscription",
          to: "/subscription",
          disabled: requiresPaidUpgrade
        },
        {
          icon: "mdi-credit-card-outline",
          title: "Billing",
          to: "/billing",
          disabled: requiresPaidUpgrade
        },
        { icon: "mdi-currency-usd", title: "Pricing", to: "/pricing" },
        {
          icon: "mdi-gift-outline",
          title: "Invite Friends",
          to: "/invite-friends",
          visible: this.referralsEnabled && !requiresPaidUpgrade
        },
        { icon: "mdi-help-circle-outline", title: "FAQ", to: this.$config && this.$config.SUPPORT_PATH || "/faq" }
      ];
      return items.filter(function (item) {
        return item.visible !== false;
      });
    },
    // Admin-only entries - rendered inside the account menu.
    adminLinks() {
      return [
        {
          icon: "mdi-shield-account-outline",
          title: "Admin",
          to: "/admin"
        },
        {
          icon: "mdi-email-fast-outline",
          title: "Send Email",
          to: "/admin/email"
        },
        { icon: "mdi-bug-outline", title: "Issues", to: "/admin/issues" },
        { icon: "mdi-server-outline", title: "Ops", to: "/admin/ops" }
      ].filter(item => this.$store.getters.isPlatformAdmin || (item.to !== "/admin/email" && item.to !== "/admin/ops"));
    },
    userInitials() {
      if (!this.currentUser || !this.currentUser.name) return "";

      const nameParts = this.currentUser.name.split(" ");
      if (nameParts.length === 1) {
        // If only one name, use first two letters of that name
        const name = nameParts[0];
        return name.length >= 2
          ? name.charAt(0).toUpperCase() + name.charAt(1).toUpperCase()
          : name.charAt(0).toUpperCase();
      } else {
        // If multiple names, use first letter of first and last name
        return (
          nameParts[0].charAt(0).toUpperCase() +
          nameParts[nameParts.length - 1].charAt(0).toUpperCase()
        );
      }
    }
  },
  watch: {
    needsPlanList: {
      immediate: true,
      handler(value) {
        if (value) this.$store.dispatch("ensurePlans");
      }
    }
  },
  methods: {
    // Round the free balance for display (short jobs cost fractions).
    formatFreeCredits(value) {
      const n = Number(value);
      if (isNaN(n)) return "0";
      return parseFloat(n.toFixed(2));
    },
    goToSubscription() {
      this.$router.push("/subscription");
    },
    goToInviteFriends() {
      if (this.$route.path !== "/invite-friends") {
        this.$router.push("/invite-friends");
      }
    }
  }
};
</script>

<style lang="scss">
.app-bar {
  background: var(--toolbar-bg) !important;
  border-bottom: 1.5px solid var(--toolbar-border) !important;
  min-height: 48px !important;
  height: 48px !important;
  line-height: 48px !important;
  z-index: 300 !important;
  padding: 0 16px !important;
  flex-grow: 0 !important;
  transition: box-shadow 0.2s;
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  position: relative;
}
.app-bar .v-toolbar__content {
  min-height: 48px !important;
  height: 48px !important;
  flex-wrap: nowrap;
  width: 100% !important;
  max-width: none !important;
  margin: 0 !important;
  padding: 0 !important;
}
.toolbar-row {
  display: flex;
  align-items: center;
  width: 100%;
  position: relative;
  max-width: none;
}
.app-bar .v-icon {
  font-size: var(--font-size-base) !important;
  vertical-align: middle;
}
.toolbar-row {
  display: flex;
  align-items: center;
  width: 100%;
  position: relative;
}
.toolbar-logo {
  display: flex;
  align-items: center;
  flex: 0 0 auto;
  font-size: var(--font-size-xl);
}
.toolbar-logo-icon {
  margin-right: 6px;
  color: var(--accent) !important;
  filter: drop-shadow(0 0 8px rgba(var(--rgb-accent), 0.6));
}
.toolbar-logo-img {
  width: 28px;
  height: 28px;
  margin-right: 8px;
  border-radius: 7px;
  flex: 0 0 auto;
}
.toolbar-logo-img--mobile {
  width: 22px;
  height: 22px;
  margin-right: 6px;
  border-radius: 6px;
}
.toolbar-wordmark {
  font-family: var(--font-display) !important;
  font-weight: 700;
  letter-spacing: -0.03em;
  color: var(--text-heading);
}

/* Logo link styling */
.logo-link {
  text-decoration: none;
  color: inherit;
  display: flex;
  align-items: center;
}

.logo-link:hover {
  text-decoration: none;
}

/* Beta Badge Styling */
.beta-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--gradient-button);
  color: var(--button-text-dark);
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  padding: 3px 10px;
  margin-left: 10px;
  border-radius: 12px;
  box-shadow: 0 2px 8px var(--shadow-primary-glow);
  vertical-align: middle;
  line-height: 1;
  position: relative;
  top: -1px;
}

.beta-badge--mobile {
  font-size: 8px;
  padding: 2px 7px;
  margin-left: 8px;
  border-radius: 10px;
}
.toolbar-left {
  display: flex;
  align-items: center;
  flex: 0 0 auto;
  z-index: 2;
}
.toolbar-nav-btn {
  margin-left: 16px;
  text-transform: none;
  letter-spacing: normal;
  font-weight: 600 !important;
  color: var(--text-primary) !important;
}
.toolbar-right {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex: 0 0 auto;
  margin-left: auto;
  gap: 8px;
  z-index: 2;
}
.toolbar-menu-slot {
  display: flex;
  align-items: center;
  flex: 0 0 auto;
}

/* Phone row: the logo takes the free space, so the balance and the buttons sit
   at the right edge even when an app hides the balance, and a long name
   truncates instead of pushing the buttons off the row. */
.logo-link--mobile {
  margin-right: auto;
  flex: 0 1 auto;
  min-width: 0;
  overflow: hidden;
}
.logo-link--mobile .toolbar-logo {
  min-width: 0;
}
.logo-link--mobile .logo-text {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Mobile-only compact balance (free conversions + credits). */
.mobile-balance {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-left: auto;
  cursor: pointer;
  flex: 0 0 auto;
}

.mobile-balance ~ .toolbar-right {
  margin-left: 0;
}

.mobile-balance-chip {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 2px 8px;
  border-radius: 999px;
  border: 1px solid var(--border-color, #23232f);
  background: var(--bg-card, rgba(255, 255, 255, 0.04));
  color: var(--text-primary, #ececf1);
  font-size: 0.75rem;
  font-weight: 700;
  line-height: 1.4;
  white-space: nowrap;
}
.plan-credits-container {
  display: flex;
  align-items: center;
  background: transparent;
  border-radius: 0;
  padding: 0 10px;
  margin-right: 10px;
  font-size: var(--font-size-sm);
  box-shadow: none;
}
.plan-section,
.credits-section {
  color: var(--button-text-light);
}
.upgrade-link {
  color: var(--text-heading) !important;
  text-decoration: underline;
  font-weight: 600;
  transition: color 0.2s;
}
.upgrade-cta {
  margin-left: 12px;
  text-transform: none;
  letter-spacing: 0;
  font-weight: 600;
}
.invite-friends-chip {
  display: inline-flex;
  align-items: center;
  margin-left: 24px;
  padding: 2px 8px;
  border-radius: 10px;
  background: rgba(251, 146, 60, 0.12);
  color: var(--text-primary);
  font-size: var(--font-size-base);
  line-height: 1.2;
  text-decoration: none;
  transition: background-color 0.15s;
  white-space: nowrap;
}
.invite-friends-chip strong {
  color: var(--color-warning-amber);
}
.invite-friends-chip:hover {
  background: rgba(251, 146, 60, 0.22);
}
.invite-friends-chip__sub {
  color: var(--text-secondary);
  margin-left: 4px;
}
.upgrade-link:hover {
  color: var(--text-cyan-bright);
}
@media (max-width: 960px) {
  .app-bar {
    padding: 0 8px !important;
  }
  .plan-credits-container {
    display: none !important;
  }
  /* Phone and tablet buttons: a 44px tap target each (the icons and the
     28px avatar stay small inside it). The bar keeps its 48px height. */
  .mobile-menu-btn,
  .toolbar-right .v-btn,
  .mobile-profile-btn {
    width: 44px !important;
    height: 44px !important;
    min-width: 44px !important;
    padding: 0 !important;
  }
  .mobile-menu-btn .v-icon,
  .toolbar-right .v-btn .v-icon {
    font-size: 20px !important;
  }
  .mobile-profile-btn {
    background: transparent !important;
    border-radius: 50% !important;
  }
  .mobile-user-avatar {
    width: 28px !important;
    height: 28px !important;
    min-width: 28px !important;
  }
  .mobile-profile-icon {
    font-size: 22px !important;
  }
}
/* Avatar styling enhancements */
.user-avatar {
  background: var(--gradient-light-hover) !important;
  border: 2px solid var(--shadow-info-subtle) !important;
  box-shadow: 0 2px 8px var(--shadow-xs) !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
  position: relative;
}

.user-avatar:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px var(--shadow-sm) !important;
  border-color: var(--overlay-info-15) !important;
}

.user-avatar.authenticated {
  background: var(--gradient-primary-blue-hover) !important;
  border-color: var(--overlay-cyan-medium) !important;
}

.user-avatar.authenticated:hover {
  background: var(--gradient-primary-blue-active) !important;
  border-color: var(--overlay-cyan-border) !important;
}

.user-initials {
  color: var(--button-text-light) !important;
  text-shadow: 0 1px 2px var(--shadow-md);
  font-size: 11px !important;
  letter-spacing: 0.3px;
}

/* Enhanced icon styling */
.user-avatar .v-icon {
  opacity: 0.8;
  transition: opacity 0.3s ease;
}

.user-avatar:hover .v-icon {
  opacity: 1;
}

/* Phones: only the logo text shrinks on narrower screens. The bar height and the
   44px tap targets (the 960px block above) stay the same. */
@media (max-width: 600px) {
  .app-bar {
    padding: 0 4px !important;
  }
  .mobile-menu-btn {
    margin-right: 2px !important;
  }
  .toolbar-logo--mobile {
    font-weight: 700;
    letter-spacing: -0.8px;
    color: var(--text-heading);
    font-size: 0.95rem !important;
    display: flex;
    align-items: center;
  }
  .mobile-logo-icon {
    margin-right: 4px;
    color: var(--text-heading);
    font-size: 18px !important;
  }
  .logo-text {
    font-size: 0.95rem;
  }
  .toolbar-right {
    gap: 0;
  }
  .mobile-profile-icon {
    font-size: 22px !important;
  }
}

/* Small phones - a smaller logo */
@media (max-width: 480px) {
  .toolbar-logo--mobile {
    letter-spacing: -1px;
  }
  .mobile-logo-icon {
    font-size: 16px !important;
    margin-right: 2px;
  }
  .logo-text {
    font-size: 0.85rem;
  }
  .beta-badge--mobile {
    font-size: 7px;
    padding: 2px 5px;
    margin-left: 4px;
  }
}

/* Very small phones */
@media (max-width: 360px) {
  .logo-text {
    font-size: 0.78rem;
  }
  .beta-badge--mobile {
    font-size: 6px;
    padding: 1px 4px;
    margin-left: 3px;
  }
}
</style>
